import React from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { getUniqueListBy } from "@components/Common/Function";
import { isEmpty } from "../hooks/constants";
import styles from "@components/MarketVisitReport/MarketVisitReport.module.css";

/**
 * UserSection Component
 * User meeting/visit feedback section - rendered only for MH3 and RBDM Collaborator
 * 
 * @param {Object} formData - Form data
 * @param {Function} onChange - Handler for form field changes
 * @param {Array} userName - Array of visited user names
 * @param {Array} salesGroupUser - Sales group user list
 * @param {Array} selectUserType - Selected user types
 * @param {Function} setSelectUserType - Set selected user types
 * @param {Array} vlcList - VLC users list
 * @param {Array} vlcListType - Selected VLC users
 * @param {Function} setVlcListType - Set selected VLC users
 * @param {Object} characterLimitValidation - Character limit validation errors
 * @param {Object} formValidation - Form validation errors
 * @param {String} userType - Type of user
 * @param {Boolean} disable - Form disable state
 */
const UserSection = ({
  formData,
  onChange,
  userName,
  salesGroupUser,
  selectUserType,
  setSelectUserType,
  vlcList,
  vlcListType,
  setVlcListType,
  characterLimitValidation,
  formValidation,
  userType,
  disable,
}) => {
  // Only render for MH3 and RBDM Collaborator
  if (userType !== "MH3" && userType !== "RBDM Collaborator") {
    return null;
  }

  return (
    <>
      {/* Section Header */}
      <div className={`row m-0 ${styles.MarketVisitReport__headingBox}`}>
        <div className={`col-8 p-0 ${styles.MarketVisitReport__heading}`}>
          User Meeting/Visit feedback
        </div>
      </div>

      {/* Select User Type */}
      <label className={styles.MarketVisitReport__selectLabel}>
        Select User Type
      </label>
      <Autocomplete
        multiple
        value={selectUserType}
        name="userType"
        id="user-type-select"
        className="myDealer__categorySelect"
        options={
          !salesGroupUser || salesGroupUser?.length === 0
            ? [{ user_name: "Loading..." }]
            : getUniqueListBy(salesGroupUser, "user_name")
        }
        getOptionLabel={(option) => `${option?.user_name}`}
        onChange={(e, newValue) => {
          setSelectUserType(newValue);
          onChange({
            target: {
              name: "salesGroupUserType",
              value: newValue?.map((item) => item?.user_name).join(","),
            },
          });
        }}
        renderInput={(params) => <TextField {...params} placeholder={""} />}
        disabled={disable}
      />
      {!formData?.salesGroupUserType && formValidation?.salesGroupUserType && (
        <p className={styles.MarketVisitReport__validationErrors}>
          {formValidation?.salesGroupUserType}
        </p>
      )}

      {/* User Names Visited */}
      <div className={styles.MarketVisitReport__selectableRows}>
        <label className={styles.MarketVisitReport__selectLabel}>
          Name(s) of key User(s) visited where any issue(s) reported
        </label>
        {!isEmpty(vlcList) ? (
          <Autocomplete
            multiple
            value={vlcListType}
            id="vlc-user-select"
            className="myDealer__categorySelect"
            options={
              !vlcList || vlcList?.length === 0
                ? [{ name: "Loading...", bdecode__c: "" }]
                : getUniqueListBy(vlcList, "name")
            }
            getOptionLabel={(option) => option?.name}
            onChange={(e, newValue) => {
              setVlcListType(newValue);
              onChange({
                target: {
                  name: "userVisited",
                  value: newValue
                    ?.map((item) => `${item?.name}:${item?.bdecode__c}`)
                    .join(","),
                },
              });
            }}
            renderInput={(params) => <TextField {...params} placeholder={""} />}
            disabled={disable}
          />
        ) : (
          <input
            className={` ${styles.MarketVisitReport__selectField}`}
            style={{ paddingLeft: "8px" }}
            name="userVisited"
            value={userName?.length ? userName : formData?.userVisited}
            onChange={(e) => onChange(e)}
            maxLength={256}
            disabled={disable}
          />
        )}
      </div>

      {/* Feedback on User Loyalty */}
      <div className={styles.MarketVisitReport__selectableRows}>
        <label className={styles.MarketVisitReport__selectLabel}>
          Feedback on User Loyalty programm
          <span style={{ color: "red", fontSize: "1rem" }}>*</span>
        </label>
        <textarea
          name="feedbackOnUserLoyalty"
          value={formData?.feedbackOnUserLoyalty}
          rows="3"
          className={` ${styles.MarketVisitRepor__textArea}`}
          style={{ paddingLeft: "8px" }}
          onChange={(e) => onChange(e)}
          disabled={disable}
          maxLength={501}
        />
        {characterLimitValidation?.feedbackOnUserLoyalty && (
          <p className={styles.MarketVisitReport__validationErrors}>
            {characterLimitValidation?.feedbackOnUserLoyalty}
          </p>
        )}
        {!formData?.feedbackOnUserLoyalty &&
          formValidation?.feedbackOnUserLoyalty && (
            <p className={styles.MarketVisitReport__validationErrors}>
              {formValidation?.feedbackOnUserLoyalty}
            </p>
          )}
      </div>

      {/* Feedback on User App */}
      <div className={styles.MarketVisitReport__selectableRows}>
        <label className={styles.MarketVisitReport__selectLabel}>
          Feedback on User App
        </label>
        <textarea
          name="feedbackOnUserApp"
          value={formData?.feedbackOnUserApp}
          rows="3"
          className={` ${styles.MarketVisitRepor__textArea}`}
          style={{ paddingLeft: "8px" }}
          onChange={(e) => onChange(e)}
          disabled={disable}
          maxLength={501}
        />
        {characterLimitValidation?.feedbackOnUserApp && (
          <p className={styles.MarketVisitReport__validationErrors}>
            {characterLimitValidation?.feedbackOnUserApp}
          </p>
        )}
        {!formData?.feedbackOnUserApp && formValidation?.feedbackOnUserApp && (
          <p className={styles.MarketVisitReport__validationErrors}>
            {formValidation?.feedbackOnUserApp}
          </p>
        )}
      </div>

      {/* Other Key Observations */}
      <div className={styles.MarketVisitReport__selectableRows}>
        <label className={styles.MarketVisitReport__selectLabel}>
          Other Key Observations/Feedbacks at the User Meeting/Visit
          <span style={{ color: "red", fontSize: "1rem" }}>*</span>
        </label>
        <textarea
          name="feedbackAtUserMeeting"
          value={formData?.feedbackAtUserMeeting}
          rows="3"
          className={` ${styles.MarketVisitRepor__textArea}`}
          style={{ paddingLeft: "8px" }}
          onChange={(e) => onChange(e)}
          disabled={disable}
          maxLength={501}
        />
        {characterLimitValidation?.feedbackAtUserMeeting && (
          <p className={styles.MarketVisitReport__validationErrors}>
            {characterLimitValidation?.feedbackAtUserMeeting}
          </p>
        )}
        {!formData?.feedbackAtUserMeeting &&
          formValidation?.feedbackAtUserMeeting && (
            <p className={styles.MarketVisitReport__validationErrors}>
              {formValidation?.feedbackAtUserMeeting}
            </p>
          )}
      </div>

      {/* Observations from Redemption Center */}
      <div className={styles.MarketVisitReport__selectableRows}>
        <label className={styles.MarketVisitReport__selectLabel}>
          Observations from Redemption center visit (if any)
        </label>
        <textarea
          name="obsFromRedemptionCenter"
          value={formData?.obsFromRedemptionCenter}
          rows="3"
          className={` ${styles.MarketVisitRepor__textArea}`}
          style={{ paddingLeft: "8px" }}
          onChange={(e) => onChange(e)}
          disabled={disable}
          maxLength={501}
        />
        {characterLimitValidation?.obsFromRedemptionCenter && (
          <p className={styles.MarketVisitReport__validationErrors}>
            {characterLimitValidation?.obsFromRedemptionCenter}
          </p>
        )}
        {!formData?.obsFromRedemptionCenter &&
          formValidation?.obsFromRedemptionCenter && (
            <p className={styles.MarketVisitReport__validationErrors}>
              {formValidation?.obsFromRedemptionCenter}
            </p>
          )}
      </div>
    </>
  );
};

export default UserSection;
